import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./Main.css";

const heroImage = "/Images/hero-farmland-CmDJq3v0.webp";

const farmerImage = "/Images/farmer.webp";

const fieldImage = "/Images/field.webp";

const intelligenceCards = [
  {
    icon: "⌁",
    tag: "LIVE",
    title: "Crop Health Monitoring",
    text: "Track crop health using satellite and drone intelligence to identify disease, pest stress and nutrient deficiencies early.",
  },

  {
    icon: "⌖",
    tag: "FIELD",
    title: "Geo Location",
    text: "Get precise field-level insights and recommendations based on your farm location, boundaries and crop conditions.",
  },

  {
    icon: "☁",
    tag: "7 DAYS",
    title: "Weather Forecast",
    text: "Plan irrigation, spraying and harvesting with accurate weather forecasts designed around your farm.",
  },

  {
    icon: "✳",
    tag: "LOCAL",
    title: "Hyperlocal Weather",
    text: "Understand weather conditions at the farm level with localized rainfall, temperature and wind intelligence.",
  },

  {
    icon: "≋",
    tag: "ALERT",
    title: "Water Stress",
    text: "Identify water-stressed areas early and take corrective action before crop stress becomes visible.",
  },

  {
    icon: "⚗",
    tag: "EARLY ACTION",
    title: "Crop Clinic",
    text: "Identify crop problems faster with technology-assisted diagnosis. Understand symptoms, possible causes and practical next steps before an issue spreads across the field.",
  },

  {
    icon: "♧",
    tag: "SOIL INSIGHT",
    title: "Soil Health",
    text: "Understand soil conditions, nutrients and organic carbon so you can plan inputs more carefully, improve soil health and support stronger crop performance.",
  },

  {
    icon: "⌁",
    tag: "FIELD MONITORING",
    title: "Crop Health",
    text: "Monitor crop performance across your field and spot changes early with satellite intelligence, helping you respond before small problems become avoidable losses.",
  },
];

const steps = [
  {
    number: "1",
    title: "Map your farmland",
    text: "Drop a pin or draw your boundary. We match your plot to our satellite grid within minutes.",
  },
  {
    number: "2",
    title: "We read the field",
    text: "Every satellite pass is processed for crop health, soil moisture, water stress and weather.",
  },
  {
    number: "3",
    title: "Get plain advice",
    text: "Clear recommendations arrive in your regional language, by app and by SMS.",
  },
  {
    number: "4",
    title: "Act and verify",
    text: "Adjust irrigation or inputs, then use the next pass to confirm whether the field responded.",
  },
];

const testimonials = [
  {
    quote:
      "Earlier we used to decide irrigation mostly by looking at the field. Now Farmology gives us an idea where the crop is needing more water. It makes our planning much easier.",
    name: "Ramesh Patil",
    role: "Farmer · Nashik, Maharashtra",
    initials: "RP",
  },
  {
    quote:
      "For our FPO, explaining technical data to farmers was not always easy. Farmology reports are simple, so our field team can understand it and explain the same to farmers without much problem.",
    name: "Pradip Kumar",
    role: "FPO Coordinator · Bardhaman, West Bengal",
    initials: "PK",
  },
  {
    quote:
      "The weather information helped us plan the harvesting better. We were expecting rain, so we finished the work a little early. Pehle last moment pe pata chalta tha, now we can plan properly.",
    name: "Amit Verma",
    role: "Wheat Farmer · Karnal, Haryana",
    initials: "AV",
  },
  {
    quote:
      "In one field, every area is not having the same problem. Farmology helps us see which part needs attention. So we don't have to treat the whole field in the same way.",
    name: "Suresh Pawar",
    role: "Farmer · Pune, Maharashtra",
    initials: "SP",
  },
  {
    quote:
      "At first the satellite reports were something new for us. But after using it, we understood that some crop problems can be seen earlier. It gives us a better idea of what is happening in the field.",
    name: "Manoj Kumar",
    role: "Farmer Producer · Bihar",
    initials: "MK",
  },
  {
    quote:
      "Our field staff can understand the recommendations quite easily. The language is simple and we can directly tell the farmer what action should be taken. This is very useful for our team.",
    name: "Amandeep Singh",
    role: "Agriculture Coordinator · Punjab",
    initials: "AS",
  },
  {
    quote:
      "Weather, crop health and water information is available in one place. Earlier we were checking different things separately. Ab thoda time bhi save hota hai and decisions are faster.",
    name: "Krishna Rao",
    role: "Farmer · Warangal, Telangana",
    initials: "KR",
  },
  {
    quote:
      "Earlier we were treating the complete farm more or less in the same way. Now we get a clearer picture of where the crop is weak. It helps us use inputs more carefully.",
    name: "Mahesh Yadav",
    role: "Farmer · Indore, Madhya Pradesh",
    initials: "MY",
  },
  {
    quote:
      "The reports are not very complicated. Our field team can look at them and understand which farm needs attention. For us, it is useful because it is practical, not only data.",
    name: "Naresh Sharma",
    role: "FPO Coordinator · Jaipur, Rajasthan",
    initials: "NS",
  },
  {
    quote:
      "During the season there is lot of variation in the crop. The satellite view gives us a better understanding of which area is doing good and where we should check once more.",
    name: "Vijay Kumar",
    role: "Farmer · Patna, Bihar",
    initials: "VK",
  },
  {
    quote:
      "Before spraying, weather is very important. Once rain came and our whole day's work got disturbed. Now we check the forecast and plan accordingly. It is a small thing but very helpful.",
    name: "Sanjay Patil",
    role: "Farmer · Kolhapur, Maharashtra",
    initials: "SP",
  },
  {
    quote:
      "Technology is useful only when farmers can understand it. Farmology takes the complex information and makes it easier for the field team to use. That is what I like about it.",
    name: "Rahul Das",
    role: "Agriculture Advisor · Siliguri, West Bengal",
    initials: "RD",
  },
];

const faqs = [
  [
    "Which crops and farms can Farmology support?",
    "Farmology is built to support different crops, locations and farming conditions. The usefulness of each insight depends on the crop, field, available data and local conditions.",
  ],
  [
    "Do I need a smartphone all day?",
    "No. Farmology can share important farm updates through notifications and email, including updates related to your farm reports. You can also open the app whenever it is convenient for you to check your latest farm information and insights.",
  ],
  [
    "How does Farmology use satellite data?",
    "Farmology combines satellite information with weather and field-level context to create practical insights. Accuracy and availability can vary by location, data source and the type of information being analyzed.",
  ],
  [
    "How is my farm information handled?",
    "Farm and field information should be handled with appropriate privacy and security practices. Specific data handling depends on the service and information collected.",
  ],
  [
    "Can FPOs and agricultural organizations use Farmology?",
    "Yes. Farmology can be useful for individual farmers as well as FPOs, cooperatives and agriculture teams that need field-level intelligence and practical recommendations.",
  ],
];

const incubators = [
  {
    logo: "/Images/Incubators/logo1.png",
  },
  {
    logo: "/Images/Incubators/logo2.png",
  },
  {
    logo: "/Images/Incubators/logo3.png",
  },
  {
    logo: "/Images/Incubators/logo4.png",
  },
  {
    logo: "/Images/Incubators/logo5.png",
  },
  {
    logo: "/Images/Incubators/logo6.png",
  },
  {
    logo: "/Images/Incubators/logo7.png",
  },
  {
    logo: "/Images/Incubators/logo8.png",
  },
];

function Icon({ children, size = 20, stroke = 1.8 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function Arrow({ direction = "right" }) {
  if (direction === "left") {
    return (
      <Icon size={17}>
        <path d="M19 12H5" />
        <path d="m11 18-6-6 6-6" />
      </Icon>
    );
  }

  return (
    <Icon size={17}>
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </Icon>
  );
}

function App() {
  const [openFaq, setOpenFaq] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const nextTestimonial = () => {
    setActiveTestimonial((current) =>
      current === testimonials.length - 1 ? 0 : current + 1,
    );
  };

  const previousTestimonial = () => {
    setActiveTestimonial((current) =>
      current === 0 ? testimonials.length - 1 : current - 1,
    );
  };

  return (
    <main className="farmology-page">
      {/* =====================================================
          HERO
      ===================================================== */}

      <section
        id="home"
        className="hero"
        style={{
          "--hero-image": `url("${heroImage}")`,
        }}
      >
        <div className="hero-overlay" />

        <div className="container hero-content">
          <div className="eyebrow eyebrow-light">
            SMART FARMING, GROUNDED IN INDIA
          </div>

          <h1>
            Transforming
            <br />
            Agriculture with
            <br />
            Technology
          </h1>

          <p className="hero-description">
            Empowering farmers with data-driven crop insights, weather
            intelligence and practical agricultural solutions for a more
            productive and sustainable future.
          </p>

          <div className="hero-actions">
            <Link to="/contact" className="btn btn-primary">
              Connect with Us
              <Arrow />
            </Link>

            <Link to="/user/login" className="btn btn-ghost">
              Get Started
              <Arrow />
            </Link>
          </div>

          <div className="hero-stats">
            <div className="hero-stat">
              <strong>55,000+</strong>
              <span>Farmers supported</span>
            </div>

            <div className="hero-stat">
              <strong>49+</strong>
              <span>Acres monitored</span>
            </div>

            <div className="hero-stat">
              <strong>6+</strong>
              <span>Regions reached</span>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          PROMISE STRIP
      ===================================================== */}

      <section className="promise-strip">
        <div className="container promise-track">
          <div className="promise-item">
            <span className="promise-icon">⌁</span>
            <span>Better decisions for smarter, more efficient farming</span>
          </div>

          <div className="promise-item">
            <span className="promise-icon">◉</span>
            <span>Satellite intelligence for every field</span>
          </div>

          <div className="promise-item">
            <span className="promise-icon">✓</span>
            <span>Smarter practices that protect soil and water</span>
          </div>
        </div>
      </section>

      {/* =====================================================
          ABOUT
      ===================================================== */}

      <section id="about" className="about section">
        <div className="container about-grid">
          <div className="about-visual">
            <div className="farmer-photo">
              <img src={farmerImage} alt="Farmer using Farmology technology" />
            </div>

            <div className="accuracy-card">
              <strong>93%</strong>
              <span>Advisory accuracy in the field</span>
            </div>
          </div>

          <div className="about-copy">
            <div className="eyebrow">ABOUT FARMOLOGY</div>

            <h2>Technology in the hands of the farmer</h2>

            <p className="lead">
              Farmology combines satellite intelligence, weather data and
              practical agricultural knowledge to help farmers understand what
              is happening across every part of their field.
            </p>

            <p className="lead secondary-lead">
              From crop health and soil conditions to water stress, weather and
              market decisions, our goal is to turn complex farm information
              into simple, useful actions that improve productivity and support
              more sustainable farming.
            </p>

            <Link to="/service" className="text-button">
              Learn More
              <Arrow />
            </Link>
          </div>
        </div>
      </section>

      {/* =====================================================
          INTELLIGENCE
      ===================================================== */}

      <section id="service" className="intelligence section section-grid">
        <div className="container">
          <div className="section-heading centered">
            <div className="eyebrow">EIGHT INTELLIGENCE LAYERS</div>

            <h2>Field intelligence built around every farming decision</h2>

            <p>
              Farmology brings satellite, weather, location and field
              intelligence together so farmers can monitor conditions,
              understand risks and make better decisions at the right time.
            </p>
          </div>

          <div className="intelligence-grid">
            {intelligenceCards.map((card) => (
              <article className="intel-card" key={card.title}>
                <div className="intel-top">
                  <span className="intel-icon">{card.icon}</span>

                  <span className="intel-tag">{card.tag}</span>
                </div>

                <h3>{card.title}</h3>

                <p>{card.text}</p>
              </article>
            ))}
          </div>

          <div className="center-action">
            <button
              className="text-button"
              onClick={() => scrollTo("how-it-works")}
            >
              See how it works
              <Arrow />
            </button>
          </div>
        </div>
      </section>

      {/* =====================================================
          HOW IT WORKS
      ===================================================== */}

      <section id="how-it-works" className="how section">
        <div className="container how-grid">
          <div className="how-visual">
            <div
              className="how-image"
              style={{
                "--field-image": `url("${fieldImage}")`,
              }}
            >
              <div className="how-image-overlay" />

              <div className="satellite-badge">
                <span className="satellite-dot" />
                SATELLITE ANALYSIS
              </div>

              <div className="field-location-card">
                <span className="field-location-icon">
                  <Icon size={17}>
                    <path d="M12 21s7-5.2 7-11a7 7 0 1 0-14 0c0 5.8 7 11 7 11Z" />
                    <circle cx="12" cy="10" r="2.2" />
                  </Icon>
                </span>

                <span>
                  <strong>Your farmland</strong>
                  <small>Field successfully mapped</small>
                </span>
              </div>
            </div>
          </div>

          <div className="how-copy">
            <div className="eyebrow">HOW IT WORKS</div>

            <h2>From satellite data to practical action</h2>

            <p className="lead">
              Start with your farmland. Farmology combines available satellite,
              weather and field information, processes it into useful insights,
              and helps you decide what action to take next.
            </p>

            <div className="steps">
              {steps.map((step, index) => (
                <button
                  className={`step ${activeStep === index ? "active" : ""}`}
                  key={step.number}
                  onClick={() => setActiveStep(index)}
                >
                  <span className="step-number">{step.number}</span>

                  <span className="step-content">
                    <strong>{step.title}</strong>
                    <small>{step.text}</small>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          STATS BAND
      ===================================================== */}

      <section className="stats-band">
        <div className="container stats-band-inner">
          <div className="stats-quote">
            <p>
              Better information leads to better decisions and stronger outcomes
              for every field.
            </p>
          </div>
        </div>
      </section>

      {/* =====================================================
          TESTIMONIALS
      ===================================================== */}

      <section className="stories section">
        <div className="container">
          <div className="section-heading centered">
            <div className="eyebrow">FROM THE FIELD</div>

            <h2>Built for decisions that matter in the field</h2>

            <p>
              The value of farm intelligence is simple: it should help farmers
              notice problems earlier, use resources wisely and act with more
              confidence.
            </p>
          </div>

          {/* Desktop / Tablet Scrollable Testimonials */}
          <div className="testimonial-scroll-wrap">
            <div className="testimonial-grid desktop-testimonials">
              <div className="testimonial-track-set">
                {testimonials.map((item) => (
                  <article className="testimonial" key={`first-${item.name}`}>
                    <p>“{item.quote}”</p>
                    <div className="person">
                      <span className="avatar">{item.initials}</span>
                      <span className="person-details">
                        <strong>{item.name}</strong>
                        <small>{item.role}</small>
                      </span>
                    </div>
                  </article>
                ))}
              </div>

              <div className="testimonial-track-set" aria-hidden="true">
                {testimonials.map((item) => (
                  <article className="testimonial" key={`second-${item.name}`}>
                    <p>“{item.quote}”</p>
                    <div className="person">
                      <span className="avatar">{item.initials}</span>
                      <span className="person-details">
                        <strong>{item.name}</strong>
                        <small>{item.role}</small>
                      </span>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
          {/* Mobile Carousel */}
          <div className="testimonial-mobile">
            <div className="testimonial-carousel-window">
              <div
                className="testimonial-carousel-track"
                style={{
                  transform: `translateX(-${activeTestimonial * 100}%)`,
                }}
              >
                {testimonials.map((item) => (
                  <div className="testimonial-slide" key={item.name}>
                    <article className="testimonial testimonial-mobile-card">
                      <p>“{item.quote}”</p>

                      <div className="person">
                        <span className="avatar">{item.initials}</span>

                        <span className="person-details">
                          <strong>{item.name}</strong>
                          <small>{item.role}</small>
                        </span>
                      </div>
                    </article>
                  </div>
                ))}
              </div>
            </div>

            <div className="testimonial-controls">
              <button
                className="testimonial-arrow"
                onClick={previousTestimonial}
                aria-label="Previous testimonial"
              >
                <Arrow direction="left" />
              </button>

              <div className="testimonial-dots">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    className={activeTestimonial === index ? "active" : ""}
                    onClick={() => setActiveTestimonial(index)}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>

              <button
                className="testimonial-arrow"
                onClick={nextTestimonial}
                aria-label="Next testimonial"
              >
                <Arrow />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          INNOVATORS
      ===================================================== */}

      <section className="institutions section">
        <div className="container">
          <div className="section-heading centered">
            <div className="eyebrow">OUR INCUBATORS</div>

            <h2>Working with people who are shaping Indian agriculture</h2>

            <p>
              Farmology works with an ecosystem of agriculture incubators,
              institutions and field partners to bring practical technology
              closer to farmers.
            </p>
          </div>

          <div className="institution-grid">
            {incubators.map((item) => (
              <div className="institution" key={item.logo}>
                <div className="institution-logo">
                  <img src={item.logo} alt="Farmology incubator logo" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =====================================================
          FAQ
      ===================================================== */}

      <section className="faq section">
        <div className="container faq-layout">
          <div className="faq-intro">
            <div className="eyebrow">FAQ</div>

            <h2>Questions farmers and agriculture teams ask</h2>

            <p>
              Simple answers to the questions farmers, FPOs and agriculture
              teams ask before getting started.
            </p>

            <div className="faq-help-card">
              <span className="faq-help-icon">?</span>

              <div>
                <strong>Still have a question?</strong>

                <p>
                  Our team is happy to help you understand the right solution
                  for your farm.
                </p>

                <Link to="/contact" className="faq-contact-link">
                  Talk to our team
                  <Arrow />
                </Link>
              </div>
            </div>
          </div>

          <div className="faq-list">
            {faqs.map(([question, answer], index) => (
              <div
                className={`faq-item ${openFaq === index ? "open" : ""}`}
                key={question}
              >
                <button
                  className="faq-question"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  aria-expanded={openFaq === index}
                >
                  <span className="faq-question-number">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="faq-question-text">{question}</span>

                  <span className="faq-toggle">
                    {openFaq === index ? "−" : "+"}
                  </span>
                </button>

                <div className="faq-answer">
                  <p>{answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =====================================================
          CTA
      ===================================================== */}

      <section id="contact" className="cta-section">
        <div className="container">
          <div className="cta-box">
            <div className="cta-copy">
              <div className="cta-eyebrow">START TODAY</div>

              <h2>Ready to make your next farm decision smarter?</h2>

              <p>
                Start by understanding your field better and discover how
                Farmology can help you monitor crop health, weather and water
                conditions.
              </p>
            </div>

            <div className="cta-actions">
              <Link to="/service" className="btn btn-cta-light">
                Explore Services
                <Arrow />
              </Link>

              <Link to="/contact" className="btn btn-cta-white">
                Connect with Us
                <Arrow />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default App;
